import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuOpen } from '@mui/icons-material';
import { AppBar, IconButton, Stack, Toolbar, Tooltip, Typography } from '@mui/material';

import { focusFirstFocusable } from '@/utils/focusFirstFocusable';
import { useAppBarHeading } from '../AppBarHeading/useAppBarHeading';
import { useMainLayoutContext } from '../Context/useMainLayoutContext';

export const AppBarV2: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const { heading } = useAppBarHeading();
  const {
    isDrawerOpen,
    toggleDrawer,
    navigationDrawerPaperId,
    navigationMenuButtonRef,
  } = useMainLayoutContext();

  const tooltipRef = useRef<{ forceUpdate: () => void } | null>(null);
  const shouldFocusDrawerRef = useRef(false);
  const navigationMenuLabel = isDrawerOpen ? t('closeNavigationMenu') : t('openNavigationMenu');

  useEffect(() => {
    if (!shouldFocusDrawerRef.current || !isDrawerOpen) {
      return;
    }

    shouldFocusDrawerRef.current = false;

    const focusDrawer = () => {
      focusFirstFocusable(document.getElementById(navigationDrawerPaperId));
    };

    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(focusDrawer);
    });

    return () => cancelAnimationFrame(frameId);
  }, [isDrawerOpen, navigationDrawerPaperId]);

  const handleToggleDrawer = () => {
    const willOpen = !isDrawerOpen;

    if (willOpen) {
      shouldFocusDrawerRef.current = true;
    } else {
      navigationMenuButtonRef.current?.focus();
    }

    toggleDrawer(willOpen);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{ background: 'transparent' }}
      onTransitionEnd={() => tooltipRef.current?.forceUpdate()}
    >
      <Toolbar
        sx={(theme) => ({
          p: '0px !important',
          mx: { xs: 2, md: 3 },
          borderBottom: theme.borders.default,
          justifyContent: 'space-between',
        })}
      >
        <Stack direction="row" alignItems="center">
          <Tooltip
            title={navigationMenuLabel}
            slotProps={{
              popper: {
                popperRef: (ref) => {
                  tooltipRef.current = ref;
                },
              },
            }}
          >
            <IconButton
              ref={navigationMenuButtonRef}
              onClick={handleToggleDrawer}
              aria-expanded={isDrawerOpen}
              aria-controls={navigationDrawerPaperId}
              aria-label={navigationMenuLabel}
            >
              {isDrawerOpen ? <MenuOpen /> : <Menu />}
            </IconButton>
          </Tooltip>

          <Typography
            variant="h6"
            component="h1"
            sx={(theme) => ({
              color: theme.palette.text.primary,
              ml: 2.5,
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.fontWeightMedium,
              fontSize: '18px',
              lineHeight: '160%',
              letterSpacing: '0.15px',

              '&:empty': { display: 'none' },
            })}
          >
            {heading}
          </Typography>
        </Stack>

        {children}
      </Toolbar>
    </AppBar>
  );
};
